import { useEffect, useState } from 'react';
import { customerDuplicatesApi } from '../services/customerDuplicatesApi';

/**
 * The "N new duplicate candidates" navigation badge (P5): fetches only when the user can view
 * the page, and fails silently to 0 so the badge stays hidden on any error and before the first
 * nightly run.
 *
 * The count is pairs the LATEST run saw for the first time AND nobody has decided — a case Roman
 * or Gabi has already closed never comes back into the badge, even if a later run finds it again.
 * That rule lives server-side in `attention-summary.ts`; this hook only displays it.
 */
export function useCustomerDuplicateAttention(enabled: boolean): {
    count: number;
    lastRunAt: string | null;
    lastRunStatus: string | null;
} {
    const [count, setCount] = useState(0);
    const [lastRunAt, setLastRunAt] = useState<string | null>(null);
    const [lastRunStatus, setLastRunStatus] = useState<string | null>(null);

    useEffect(() => {
        if (!enabled) {
            setCount(0);
            setLastRunAt(null);
            setLastRunStatus(null);
            return;
        }
        let cancelled = false;
        customerDuplicatesApi
            .attentionSummary()
            .then(res => {
                if (cancelled) return;
                setCount(res.newSinceLastRun ?? 0);
                setLastRunAt(res.lastRun?.finishedAt ?? res.lastRun?.startedAt ?? null);
                setLastRunStatus(res.lastRun?.status ?? null);
            })
            .catch(() => {
                if (cancelled) return;
                setCount(0);
                setLastRunAt(null);
                setLastRunStatus(null);
            });
        return () => {
            cancelled = true;
        };
    }, [enabled]);

    return { count, lastRunAt, lastRunStatus };
}
